/**
 * iPIDiagram Main Application Component
 */

import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useLivePlotPrintImages, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './api';
import type { ChartKey, PIDiagramResultState } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, LIVE_PLOT_CARD_MIN_HEIGHT, LIVE_PLOT_SIZE_BY_KEY, LOAD_TYPES, PRINT_CHART_ASPECT, PRINT_CHART_IMAGE_OPTIONS, PRINT_CHART_ORDER } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, GenerateLinkButton, LivePlot, PrintChartImageGrid, RichSummaryReport } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  // Check for calcId first (short URL from CalcPad)
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  const initialValues: FormValues = useMemo(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'PI Diagram',
          length: '10',
          tribWidth: '5',
          hingeLength: '5',
          totalWeight: '5*10*12',
          klmFactor: '0.72',
          Ru: '9',
          Xe: '1.0',
          loadType: '0',
          Pa: '1, 4, 10',
          Ia: '10, 28, 89',
          ductilityLimit: '1, 3, 12, 25',
          rotationLimit: '3, 3, 10, 20'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({ schema, defaultValues: initialValues });

  const values = watch();
  const { saveDataToDB } = useAppStorage<FormValues, PIDiagramResultState>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, PIDiagramResultState>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });

  const { chartRefs, printRootRef } = useLivePlotPrintImages<ChartKey, PIDiagramResultState>({
    analysisResult,
    setAnalysisResult,
    getImageOptions: key => PRINT_CHART_IMAGE_OPTIONS[key],
    errorMessage: 'Unable to prepare PI diagram chart for printing. Please wait for the chart to finish rendering and try again.',
    enableScreenshot: true
  });

  // Load full state from IndexedDB if calcId is present
  useSavedCalculationLoader<FormValues, PIDiagramResultState>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });
  const fields = defineFields<FormValues>([
    { kind: 'input', name: 'title', label: 'Title', helpText: 'Enter the title of the component', placeholder: 'Component Title' },
    { kind: 'input', name: 'length', label: 'Length', unit: 'ft', helpText: 'Enter the length of the component', placeholder: 'Length' },
    { kind: 'input', name: 'tribWidth', label: 'Tributary Width', unit: 'ft', helpText: 'Enter the tributary width', placeholder: 'Tributary Width' },
    { kind: 'input', name: 'hingeLength', label: 'Hinge Length', unit: 'ft', helpText: 'Enter the hinge length', placeholder: 'Hinge Length' },
    { kind: 'input', name: 'totalWeight', label: 'Total Weight', unit: 'lbs', helpText: 'Enter the total weight', placeholder: 'Total Weight' },
    { kind: 'input', name: 'klmFactor', label: 'KLM Factor', unit: '', helpText: 'Enter the KLM factor', placeholder: 'KLM Factor' },
    { kind: 'input', name: 'Ru', label: 'Ultimate Resistance', unit: 'kips', helpText: 'Enter the ultimate resistance', placeholder: 'Ultimate Resistance' },
    { kind: 'input', name: 'Xe', label: 'Elastic Displacement', unit: 'in', helpText: 'Enter the elastic displacement', placeholder: 'Elastic Displacement' },
    { kind: 'dropdown', name: 'loadType', label: 'Load Type', options: LOAD_TYPES, helpText: 'Select the load type...' },
    { kind: 'input', name: 'Pa', label: 'Pressure Values', unit: 'psi', helpText: 'Comma-separated pressure values', placeholder: '5.0, 10.0' },
    { kind: 'input', name: 'Ia', label: 'Impulse Values', unit: 'psi-msec', helpText: 'Comma-separated impulse values', placeholder: '20.0, 40.0' },
    { kind: 'input', name: 'ductilityLimit', label: 'Ductility Limit', unit: '', helpText: 'Comma-separated ductility values', placeholder: '1, 2, 5, 10' },
    { kind: 'input', name: 'rotationLimit', label: 'Rotation Limit', unit: 'deg', helpText: 'Comma-separated rotation values', placeholder: '2, 4, 6, 12' }
  ]);
  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">PI diagram analysis</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex flex-row w-full mt-1 gap-2">
            <button className="submit-button" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid min-w-0 grid-rows-[auto_1fr] rounded-xl gap-y-0 w-full">
          <div className="analysis-app__results shadow-div rounded-xl m-1 min-w-0 overflow-hidden">
            {analysisResult?.summary ? <RichSummaryReport summary={analysisResult.summary} className="rounded-lg border bg-white p-4 text-[13px] leading-5 text-slate-800" /> : <div className="p-4 text-black-500 italic">{analysisResult ? 'Saved result does not include a summary. Re-run the analysis to generate it.' : 'No analysis results yet.'}</div>}
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.plotData && (
              <div className="analysis-app__charts-grid grid grid-cols-1 gap-3 m-2 sm:m-4 print:gap-1">
                {Object.entries(analysisResult.plotData)
                  .filter(([, plot]) => !!plot)
                  .map(([key, plot]) => {
                    const chartKey = key as ChartKey;

                    return (
                      <div key={key} className="analysis-app__chart-card flex min-w-0 items-center justify-center break-inside-avoid" style={{ minHeight: LIVE_PLOT_CARD_MIN_HEIGHT }}>
                        <LivePlot
                          ref={node => {
                            chartRefs.current[chartKey] = node;
                          }}
                          plot={plot}
                          {...LIVE_PLOT_SIZE_BY_KEY[chartKey]}
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        <div className="print:hidden hidden lg:block"></div>
      </div>

      {/* ---- PRINT PORTAL ---- */}
      <PrintPortal>
        <div ref={printRootRef} className="print-root">
          <div className="print-a4-landscape border border-black box-border bg-white">
            <div className="print-page-content hidden print:grid h-full min-h-0 grid-cols-[24%_76%] print:bg-white print:text-black">
              <div className="flex flex-col h-full min-h-0">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'iPI Diagram'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    <RichSummaryReport summary={analysisResult?.summary} className="text-[9.5px] leading-[1.18] text-black" />
                  </div>
                </div>

                {/* Shareable Link at Bottom */}
                <div className="grid grid-cols-2 mt-auto p-1 border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-[9px] text-blue-600 underline font-semibold hover:text-blue-800" target="_blank" rel="noopener noreferrer" title="Click to open shareable link">
                    🔗 LINK
                  </a>
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none text-blue-500">{`${APP_NAME} V${APP_VERSION}`}</div>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="flex h-full min-h-0 items-center justify-center overflow-hidden p-2">
                  <PrintChartImageGrid charts={analysisResult?.charts} order={PRINT_CHART_ORDER} aspectByKey={PRINT_CHART_ASPECT} className="w-full max-w-[150mm]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
